import {createContext, useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useUserApi} from '../api/user';
import {secondsRangeBetweenPhoneVerificationPrompts, showVerificationPages} from '../constants';
import AuthContext from './AuthContext';

const MiscContext = createContext();
export default MiscContext;

/**
 * Context of miscellanea data used for any purpose and
 * also to call some endpoints once and use that value everywhere.
 *
 */
export const MiscProvider = ({children}) => {
  const userObject = localStorage.getItem('userObject') || {};
  const [animationDirection, setAnimationDirection] = useState('');
  const [userFirstName, setUserFirstName] = useState(userObject?.firstName ?? '');
  const [userLastName, setUserLastName] = useState(userObject?.lastName ?? '');
  const [userPhoneNumber, setUserPhoneNumber] = useState(userObject?.phoneNumber ?? '');
  const [userEmail, setUserEmail] = useState(userObject?.email ?? '');
  const [userPlan, setUserPlan] = useState(userObject?.plan ?? '');
  const [isEmailSubscribed, setIsEmailSubscribed] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const {authTokens} = useContext(AuthContext);
  const navigate = useNavigate();

  const userData = useUserApi();
  useEffect(() => {
    //assuming an authenticated user will always have a user email filled
    if (authTokens === null || userEmail !== '') return;
    getUserDataIntoMiscContext();
    getUserCreditsDetail();
  }, [authTokens]);

  const getUserCreditsDetail = async () =>  {
    const userDataCr = await userData.getUserCredits();
    setUserCredits(userDataCr.data.user_credit)

  }

  const getUserDataIntoMiscContext = async withoutRedirections => {
    const userDataReq = await userData.getUserData();
    if (userDataReq.status !== 200) return;

    setUserFirstName(userDataReq.data.first_name);
    setUserLastName(userDataReq.data.last_name ?? '');
    setUserPlan(userDataReq.data.plan);
    setUserPhoneNumber(userDataReq.data.phone_number);
    setUserEmail(userDataReq.data.email);
    setIsEmailSubscribed(userDataReq.data.is_email_subscribed);

    const userObject = {
      firstName: userDataReq.data.first_name,
      lastName: userDataReq.data.last_name,
      plan: userDataReq.data.plan,
      phoneNumber: userDataReq.data.phone_number,
      email: userDataReq.data.email,
      isEmailSubscribed: userDataReq.data.is_email_subscribed,
    };
    localStorage.setItem('userObject', JSON.stringify(userObject));

    if (!showVerificationPages || withoutRedirections) return;

    if (
      userDataReq.data.is_phone_verified ||
      userDataReq.data.phone_number === null ||
      userDataReq.data.phone_number === ''
    ) {
      return;
    }

    if (!userDataReq.data.hasOwnProperty('last_prompted_to_verify_phone')) {
      navigate('/verify-phone');
      return;
    }
    const lastDatePromptedToVerifyPhone = new Date(userDataReq.data.last_prompted_to_verify_phone);
    const thisMoment = new Date();
    const secondsDifference = Math.abs(thisMoment.getTime() - lastDatePromptedToVerifyPhone.getTime()) / 1000;

    if (secondsDifference >= secondsRangeBetweenPhoneVerificationPrompts) {
      navigate('/verify-phone');
    }
  };

  const contextData = {
    animationDirection,
    setAnimationDirection,
    userFirstName,
    setUserFirstName,
    userLastName,
    setUserLastName,
    userPlan,
    setUserPlan,
    userPhoneNumber,
    setUserPhoneNumber,
    isEmailSubscribed,
    setIsEmailSubscribed,
    userEmail,
    getUserDataIntoMiscContext,
    userCredits,
    setUserCredits
  };

  return <MiscContext.Provider value={contextData}>{children}</MiscContext.Provider>;
};
